/**
 * Events that leave the chat frame for the host page's Adobe.
 *
 * The chat frame cannot call the host's `linkTracking` itself, so each moment is posted to
 * the parent window as one generic analytics envelope and the web team's listener calls
 * `linkTracking({ linkName })` on receipt. Same route, same guard and same envelope style as
 * the proven `live_agent_connected` message in `liveAgentAttribution.ts`; that message is
 * untouched — this ADDS beside it, never replaces it.
 *
 * Payload contract (for the web team's listener — one branch, many link names):
 *   { source: 'TRACI', type: 'analytics_event', linkName, event, sessionId, timestamp, ...extra }
 *
 * Moments carried today:
 *   TRAC-965 — the four live-chat moments (`postLiveChatEvent`):
 *     available  — the FIRST verified-open availability answer, once per app mount (never per
 *                  poll, never on a fail-open answer).
 *     requested  — the customer pressed Connect. A tap on the closed desk is NOT a request.
 *     connected  — the real rep identity resolved (`agent_named`), capped at one per request
 *                  (a transfer does not re-fire; `live_agent_connected` still does, by design).
 *     ended      — a conversation we said `connected` for has finished. Ends with no
 *                  `connected` (a rep who joined but never got named) are logged, not sent.
 *   Anything else (an agent answer, a capability the answer used) goes through
 *   `postAnalyticsEvent` directly with its own `event` key and link name.
 *
 * Which moment fires when is decided at the call sites, not here. The connected latch is
 * written beside the session, like the funnel's open attempt, because the panel follows a
 * visitor from page to page: `connected` lands on one page and `ended` on another, and the
 * pair has to survive that.
 *
 * Nothing in here may throw or block — this is measurement wrapped around the highest-stakes
 * moment in the product (see the header of `services/handoffFunnel.ts`).
 */

import { trackAIEvent } from '../../utils/analytics';
import { EXPERIMENT_ENVELOPE_FIELDS, EXPERIMENT_EVENTS, STARTER_PROMPT_EXPERIMENTS } from '../../config/experimentEvents';
import { parseExperimentCode } from '../../utils/experimentParser';

export type LiveChatEvent =
  | 'live_chat_available'
  | 'live_chat_requested'
  | 'live_chat_connected'
  | 'live_chat_ended';

/** The Adobe custom-link names — final, verbatim (TRAC-965). */
export const LIVE_CHAT_LINK_NAMES: Readonly<Record<LiveChatEvent, string>> = Object.freeze({
  live_chat_available: 'tr: ai tire agent: live chat available',
  live_chat_requested: 'tr: ai tire agent: live chat requested',
  live_chat_connected: 'tr: ai tire agent: live chat connected',
  live_chat_ended: 'tr: ai tire agent: live chat ended',
});

export const LIVE_CHAT_EVENTS: readonly LiveChatEvent[] = Object.freeze([
  'live_chat_available',
  'live_chat_requested',
  'live_chat_connected',
  'live_chat_ended',
]);

export function isLiveChatEvent(value: string): value is LiveChatEvent {
  return (LIVE_CHAT_EVENTS as readonly string[]).includes(value);
}

/**
 * TRAC-968: the agent answered. Once per completed agent response stream, whatever the
 * agent did to produce it — never per tool, card or chunk. The customer's own messages,
 * a live rep's messages and the client-side routing prompt never stream through the
 * agent, so they never fire it.
 */
export const AGENT_RESPONSE_LINK_NAME = 'tr: ai tire agent: response';
export const AGENT_RESPONSE_EVENT = 'agent_response';

export interface AnalyticsEventMessage {
  source: 'TRACI';
  type: 'analytics_event';
  /** What the host passes to `linkTracking`. */
  linkName: string;
  /** The same moment as a stable machine key, for a listener that prefers to switch on it. */
  event: string;
  sessionId: string | null;
  timestamp: string;
  /** Anything the moment carries beyond its name (e.g. `responseType`). Never PII. */
  [extra: string]: unknown;
}

/** A moment to post: its machine key, its Adobe link name, and any extra fields. */
export type AnalyticsMoment = { event: string; linkName: string } & Record<string, unknown>;

/**
 * Post one analytics moment to the host page. Returns the message that was posted (for
 * logging / tests) or null when it was a no-op: not embedded, or the post itself failed.
 * The envelope's `source`, `type`, `sessionId` and `timestamp` are always ours — an extra
 * field cannot overwrite them.
 */
export function postAnalyticsEvent(
  win: Window,
  moment: AnalyticsMoment,
  sessionId: string | null,
): AnalyticsEventMessage | null {
  // TRAC-1108: while the session is in an experiment, every moment carries experimentId
  // and arm. The moment's own fields win, so an exposure names the experiment it is for.
  moment = { ...experimentEnvelope(sessionId), ...moment };
  // TRAC-965 follow-up: the host-page relay below needs a branch on tirerack.com that does
  // not exist, so the same moment is ALSO announced inside the frame, where the Adobe tag
  // that already delivers "tr: ai tire agent: response" can turn it into a beacon. The
  // announcement never depends on being embedded; the postMessage still does.
  try {
    const { event, linkName, ...extra } = moment;
    trackAIEvent(linkName, event, sessionId, extra);
  } catch {
    /* measurement never breaks the thing it measures */
  }
  try {
    if (win.parent === win) return null; // not embedded — there's no host page to notify
    const message: AnalyticsEventMessage = {
      ...moment,
      source: 'TRACI',
      type: 'analytics_event',
      sessionId,
      timestamp: new Date().toISOString(),
    };
    // NOTE: targetOrigin is '*' for now so SIT/dev parents can receive it — the same
    // standing TODO as liveAgentAttribution.ts: lock this to the tirerack.com parent
    // origin before prod, in both places at once.
    win.parent.postMessage(message, '*');
    return message;
  } catch {
    return null; // measurement never breaks the thing it measures
  }
}

/** TRAC-968: the agent completed an answer. */
export function postAgentResponse(
  win: Window,
  sessionId: string | null,
): AnalyticsEventMessage | null {
  return postAnalyticsEvent(win, { event: AGENT_RESPONSE_EVENT, linkName: AGENT_RESPONSE_LINK_NAME }, sessionId);
}

/** TRAC-965: one of the four live-chat moments, by its key. */
export function postLiveChatEvent(
  win: Window,
  event: LiveChatEvent,
  sessionId: string | null,
): AnalyticsEventMessage | null {
  return postAnalyticsEvent(win, { event, linkName: LIVE_CHAT_LINK_NAMES[event] }, sessionId);
}

// ─── the connected latch ─────────────────────────────────────────────────────────────
//
// One record, keyed by session: "we told Adobe this conversation connected". Written on
// `connected`, read by `ended` (possibly on a later page), cleared by the next Connect press
// and by every end. Storage is the truth — two tabs on the same conversation share it, so
// the first tab to send `ended` clears it for the other. The memory copy is only for a page
// whose storage is unavailable, so it can still pair its own connected and ended.

const STORE_KEY = 'traci_live_chat_connected';
/** Nobody is still talking to a rep a day later; a record that old is a stale tab. */
const STORE_TTL_MS = 24 * 60 * 60 * 1000;

interface ConnectedRecord { sessionId: string | null; at: number }

let memo: ConnectedRecord | null = null;

function readRecord(): ConnectedRecord | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORE_KEY);
  } catch {
    return memo; // no storage on this page: what this page remembers is all there is
  }
  if (!raw) return null;
  try {
    const rec = JSON.parse(raw) as Partial<ConnectedRecord>;
    if (typeof rec.at !== 'number') return null;
    return { sessionId: typeof rec.sessionId === 'string' ? rec.sessionId : null, at: rec.at };
  } catch {
    return null;
  }
}

/**
 * Remember that `connected` was emitted for this session. No session id, no key: a record
 * that any later null could match is worse than an orphaned `ended`, which is logged.
 */
export function markLiveChatConnected(sessionId: string | null): void {
  if (!sessionId) return;
  const rec: ConnectedRecord = { sessionId, at: Date.now() };
  memo = rec;
  try { localStorage.setItem(STORE_KEY, JSON.stringify(rec)); } catch { /* memory copy still works */ }
}

/** Was `connected` emitted for this session (on this page or an earlier one)? */
export function liveChatConnectedEmitted(sessionId: string | null): boolean {
  if (!sessionId) return false;
  const rec = readRecord();
  if (!rec) return false;
  if (Date.now() - rec.at > STORE_TTL_MS) { clearLiveChatConnected(); return false; } // a stale tab's leftovers
  return rec.sessionId === sessionId;
}

/** The conversation is over (or a new one is being asked for): forget the latch. */
export function clearLiveChatConnected(): void {
  memo = null;
  try { localStorage.removeItem(STORE_KEY); } catch { /* as above */ }
}

/** Test seam: fresh page AND fresh storage. */
export function _resetLiveChatAnalytics(): void {
  clearLiveChatConnected();
}

// ─── agent experiments (TRAC-1108) ──────────────────────────────────────────────────

/** An experiment and this session's arm in it, or null outside any experiment. */
export type ExperimentContext = { experimentId: string; arm: string } | null;

let experimentContextProvider: ((sessionId: string | null) => ExperimentContext) | null = null;

/**
 * Optional: lets a later experiment name its arms (e.g. "control" / "treatment") instead
 * of the session id's letter and variant number. Returning null falls back to the session
 * id. Unset by default; nothing on main sets it.
 */
export function setExperimentContextProvider(
  provider: ((sessionId: string | null) => ExperimentContext) | null,
): void {
  experimentContextProvider = provider;
}

/**
 * The experiments a session id says it is in, read the same way eVar92 carries them
 * (`session-v4a1b2-...`). One experiment gives { experimentId: "b", arm: "2" }; several
 * are comma-joined in letter order, index-aligned ("a,b" / "1,2"). Null for none.
 */
export function experimentContextFromSessionId(sessionId: string | null | undefined): ExperimentContext {
  if (!sessionId) return null;
  const assignments = parseExperimentCode(sessionId);
  const letters = Object.keys(assignments).sort();
  if (letters.length === 0) return null;
  return {
    experimentId: letters.map((l) => l.toLowerCase()).join(','),
    arm: letters.map((l) => String(assignments[l])).join(','),
  };
}

/**
 * This session's arm in each experiment whose arm decides the starter prompts
 * (STARTER_PROMPT_EXPERIMENTS). Empty when the session is in none of them.
 */
export function starterPromptExperimentArms(
  sessionId: string | null | undefined,
): Array<{ experimentId: string; arm: string }> {
  if (!sessionId) return [];
  const assignments = parseExperimentCode(sessionId);
  return STARTER_PROMPT_EXPERIMENTS
    .filter((letter) => assignments[letter.toUpperCase()] != null)
    .map((letter) => ({ experimentId: letter.toLowerCase(), arm: String(assignments[letter.toUpperCase()]) }));
}

/** The envelope fields for this session's experiments; empty outside one. Never throws. */
export function experimentEnvelope(sessionId: string | null = null): Record<string, string> {
  try {
    const ctx = experimentContextProvider?.(sessionId) ?? experimentContextFromSessionId(sessionId);
    if (!ctx || !ctx.experimentId || !ctx.arm) return {};
    return {
      [EXPERIMENT_ENVELOPE_FIELDS.experimentId]: ctx.experimentId,
      [EXPERIMENT_ENVELOPE_FIELDS.arm]: ctx.arm,
    };
  } catch {
    return {};
  }
}
const exposedThisPage = new Set<string>();

function exposureKey(sessionId: string, experimentId: string): string {
  return `traci_exp_exposed:${sessionId}:${experimentId}`;
}

/**
 * Whether this session already sent its exposure for this experiment. Held in
 * sessionStorage so a navigation or a refetch of the prompts (which re-fires when page
 * context settles) does not count the same session twice.
 */
export function exposureAlreadySent(sessionId: string, experimentId: string): boolean {
  const key = exposureKey(sessionId, experimentId);
  if (exposedThisPage.has(key)) return true;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markExposureSent(sessionId: string, experimentId: string): void {
  const key = exposureKey(sessionId, experimentId);
  exposedThisPage.add(key);
  try { sessionStorage.setItem(key, '1'); } catch { /* storage off: the in-page set still holds */ }
}

/** The arm's starter prompts rendered. Once per session per experiment; null when skipped. */
export function postExperimentExposed(
  win: Window,
  sessionId: string | null,
  detail: { experimentId: string; arm: string; pageBucket: string },
): AnalyticsEventMessage | null {
  if (!sessionId || exposureAlreadySent(sessionId, detail.experimentId)) return null;
  markExposureSent(sessionId, detail.experimentId);
  const spec = EXPERIMENT_EVENTS.experiment_exposed;
  return postAnalyticsEvent(win, { event: 'experiment_exposed', linkName: spec.linkName, ...detail }, sessionId);
}

/** A starter prompt was clicked. */
export function postStarterPromptClick(
  win: Window,
  sessionId: string | null,
  detail: { experimentId?: string; arm?: string; pageBucket: string; promptKind: string; promptId?: string },
): AnalyticsEventMessage | null {
  const spec = EXPERIMENT_EVENTS.starter_prompt_click;
  const clean = Object.fromEntries(Object.entries(detail).filter(([, v]) => v != null && v !== ''));
  return postAnalyticsEvent(win, { event: 'starter_prompt_click', linkName: spec.linkName, ...clean }, sessionId);
}

/** The ZIP and email box opened. Carries nothing beyond the envelope. */
export function postOrderLookupOpened(win: Window, sessionId: string | null): AnalyticsEventMessage | null {
  const spec = EXPERIMENT_EVENTS.order_lookup_opened;
  return postAnalyticsEvent(win, { event: 'order_lookup_opened', linkName: spec.linkName }, sessionId);
}

/** Tests only: forget which exposures this page already sent. */
export function _resetExposuresForTest(): void {
  exposedThisPage.clear();
}
